import React, { memo } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  OfferDto,
  OfferProductDto,
  QuantityButtonProductVariant,
  QuantityButtonsWidgetDto,
  WidgetDeviceType,
  WidgetRenderLocation,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { IgQuantityButtons } from "@src/domains/WidgetLibrary/QuantityButtons/IgQuantityButtons";
import { QuantityButtonProductDropdown } from "@src/features/personalizations/components/offerModifications/widgets/QuantityButtonProductDropdown";
import { UserInterfaceWidgetPreview as Preview } from "@src/features/personalizations/components/offerModifications/widgets/UserInterfaceWidgetPreview";
import { useDispatch, useSelectorFactory } from "@src/store";
import { setDialog } from "@src/store/slices/global";
import { getVariationOffer } from "@src/store/slices/offers/selectors";
import { getUserInterfaceQuantityButtons } from "@src/store/slices/widgets/selectors";

import { WidgetMoreMenu } from "./WidgetMoreMenu";

interface QuantityButtonsPreviewProps {
  variationId?: string;
  hideLabel?: boolean;
  renderLocation?: WidgetRenderLocation;
  offerProducts?: OfferProductDto[];
  widget?: QuantityButtonsWidgetDto;
  offer?: OfferDto;
}

const getInitialFocusedOfferVariant = (offerProducts: OfferProductDto[]) => {
  if (offerProducts.length > 0) {
    return {
      productId: offerProducts[0].id,
      productTitle: offerProducts[0].title,
      id: offerProducts[0].variants[0].id,
      title: offerProducts[0].variants[0].title,
      price: offerProducts[0].variants[0].price,
      compareAtPrice: offerProducts[0].variants[0].compareAtPrice,
    };
  } else return null;
};

export const QuantityButtonsPreview = ({
  variationId,
  hideLabel,
  renderLocation,
  offerProducts,
  widget: passedWidget,
  offer: passedOffer,
}: QuantityButtonsPreviewProps) => {
  const dispatch = useDispatch();
  const [focusedOfferVariant, setFocusedOfferVariant] =
    useImmer<QuantityButtonProductVariant | null>(
      getInitialFocusedOfferVariant(offerProducts || [])
    );

  const handleEdit = () => {
    dispatch(
      setDialog({
        dialog: "offers",
        open: true,
        props: { targetPage: "quantityButtons" },
      })
    );
  };

  const handleDelete = () => {
    dispatch(
      setDialog({
        dialog: "deleteComponent",
        open: true,
        props: {
          componentType: "quantityButtons",
          variationId,
        },
      })
    );
  };

  return (
    <Preview renderLocation={renderLocation}>
      {!hideLabel && (
        <Preview.Label
          label={renderLocation === "settings" ? "Preview" : "Quantity Buttons"}
          menu={
            renderLocation === "settings" ? null : (
              <WidgetMoreMenu onEdit={handleEdit} onDelete={handleDelete} />
            )
          }
        >
          {focusedOfferVariant && offerProducts ? (
            <QuantityButtonProductDropdown
              offerProducts={offerProducts}
              focusedOfferVariant={focusedOfferVariant}
              setFocusedOfferVariant={setFocusedOfferVariant}
            />
          ) : null}
        </Preview.Label>
      )}
      <Grid item>
        <Preview.Widget device={"desktop"} renderLocation={renderLocation}>
          <Widget
            focusedOfferVariant={focusedOfferVariant}
            variationId={variationId}
            device={"desktop"}
            widget={passedWidget}
            offer={passedOffer}
          />
        </Preview.Widget>
      </Grid>
    </Preview>
  );
};

interface WidgetProps {
  variationId?: string;
  device: WidgetDeviceType;
  focusedOfferVariant?: QuantityButtonProductVariant | null;
  widget?: QuantityButtonsWidgetDto;
  offer?: OfferDto;
}

const Widget = memo(
  ({
    variationId,
    device,
    focusedOfferVariant,
    widget: passedWidget,
    offer: passedOffer,
  }: WidgetProps) => {
    const storeOffer = useSelector(
      variationId ? getVariationOffer(variationId) : () => null
    );
    const storeWidget = useSelectorFactory(
      getUserInterfaceQuantityButtons,
      variationId || ""
    );

    const offer = passedOffer || storeOffer;
    const widget = passedWidget || storeWidget;

    if (!widget || !offer) return null;

    return (
      <IgQuantityButtons
        gridSx={{
          width: "350px",
        }}
        focusedOfferVariant={focusedOfferVariant}
        device={device}
        offer={offer}
        widget={widget}
      />
    );
  }
);
